// ============================================================================
// RESULTS FULL PAGE - Pro Version with Speedometer
// ALL TEXT USES TRANSLATION KEYS
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  AlertTriangle, Shield, TrendingUp, Clock, Download, 
  CheckCircle, Target, BookOpen, Briefcase, ChevronRight,
  LayoutDashboard, ArrowLeft
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';
import { generateReportPDF } from './pdfExport';
import Header from '../components/Header';

// ============================================================================
// SPEEDOMETER COMPONENT
// ============================================================================
const RiskSpeedometer = ({ score }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);
  
  const angleInDegrees = 180 - (animatedScore / 100) * 180;
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  
  const needleLength = 65;
  const centerX = 90, centerY = 90;
  const needleX = centerX + needleLength * Math.cos(angleInRadians);
  const needleY = centerY - needleLength * Math.sin(angleInRadians);
  
  return (
    <div className="relative">
      <svg width="180" height="100" viewBox="0 0 180 100">
        <defs>
          <linearGradient id="gaugeGradientFull" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e"/>
            <stop offset="25%" stopColor="#84cc16"/>
            <stop offset="50%" stopColor="#eab308"/>
            <stop offset="75%" stopColor="#f97316"/>
            <stop offset="100%" stopColor="#ef4444"/>
          </linearGradient>
        </defs>
        
        <path d="M 15 90 A 75 75 0 0 1 165 90" stroke="rgba(255,255,255,0.2)" strokeWidth="12" fill="none" strokeLinecap="round"/>
        <path d="M 15 90 A 75 75 0 0 1 165 90" stroke="url(#gaugeGradientFull)" strokeWidth="12" fill="none" strokeLinecap="round"/>
        
        <line x1={centerX} y1={centerY} x2={needleX} y2={needleY} stroke="white" strokeWidth="3" strokeLinecap="round" style={{ transition: 'all 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>
        <circle cx={centerX} cy={centerY} r="10" fill="white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}/>
        <circle cx={centerX} cy={centerY} r="5" fill="rgba(0,0,0,0.2)"/>
      </svg>
    </div>
  );
};

// ============================================================================
// RADAR CHART COMPONENT
// ============================================================================
const RadarChart = ({ data, t }) => {
  const { industryScore = 50, riskScore = 50, knowledgeScore = 50, readinessScore = 50 } = data;
  
  return (
    <div className="flex-shrink-0">
      <svg width="200" height="220" viewBox="0 0 200 220">
        <g stroke="rgba(255,255,255,0.2)" fill="none" strokeWidth="1">
          <polygon points="100,20 180,110 100,200 20,110"/>
          <polygon points="100,42.5 157.5,110 100,177.5 42.5,110"/>
          <polygon points="100,65 135,110 100,155 65,110"/>
          <polygon points="100,87.5 112.5,110 100,132.5 87.5,110"/>
          <line x1="100" y1="20" x2="100" y2="200"/>
          <line x1="20" y1="110" x2="180" y2="110"/>
        </g>
        
        <polygon 
          points={`100,${110 - (readinessScore / 100) * 80} ${100 + (industryScore / 100) * 80},110 100,${110 + (knowledgeScore / 100) * 80} ${100 - (riskScore / 100) * 80},110`}
          fill="rgba(255,255,255,0.2)" 
          stroke="rgba(255,255,255,0.8)" 
          strokeWidth="2"
        />
        
        <circle cx={100} cy={110 - (readinessScore / 100) * 80} r="5" fill="white"/>
        <circle cx={100 + (industryScore / 100) * 80} cy={110} r="5" fill="white"/>
        <circle cx={100} cy={110 + (knowledgeScore / 100) * 80} r="5" fill="white"/>
        <circle cx={100 - (riskScore / 100) * 80} cy={110} r="5" fill="white"/>
        
        <text x="100" y="12" textAnchor="middle" fill="white" fontSize="12" fontWeight="500">{t('results.radar.readiness')}</text>
        <text x="192" y="110" textAnchor="middle" fill="white" fontSize="12" fontWeight="500" transform="rotate(90, 192, 110)">{t('results.radar.industry')}</text>
        <text x="100" y="218" textAnchor="middle" fill="white" fontSize="12" fontWeight="500">{t('results.radar.knowledge')}</text>
        <text x="8" y="110" textAnchor="middle" fill="white" fontSize="12" fontWeight="500" transform="rotate(-90, 8, 110)">{t('results.radar.tasks')}</text>
      </svg>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ResultsFull = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const loadResults = async () => {
      // Try to load from localStorage first
      const storedResults = localStorage.getItem('lab2040_assessment_results');
      if (storedResults) {
        setResults(JSON.parse(storedResults));
        setLoading(false);
        return;
      }
      
      // If logged in, try to load from database
      if (user) {
        const { data } = await supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('completed_at', { ascending: false })
          .limit(1)
          .single();
        
        if (data) {
          if (data.results && typeof data.results === 'object') {
            setResults(data.results);
          } else {
            setResults({
              compositeScore: data.composite_score,
              riskLevel: data.risk_level,
              timeHorizon: data.timeline || 'MEDIUM_TERM',
              industryScore: data.industry_score || 50,
              riskScore: data.risk_score || 50,
              knowledgeScore: data.knowledge_score || 50,
              readinessScore: data.readiness_score || 50,
              keyRiskFactors: data.key_risk_factors || [],
              protectiveFactors: data.protective_factors || [],
              recommendations: data.recommendations || [],
              userType: data.user_type
            });
          }
        }
      }
      
      setLoading(false);
    };
    
    loadResults();
  }, [user]);

  const getRiskClass = (level) => {
    const classes = {
      CRITICAL: 'bg-red-100 text-red-700',
      HIGH: 'bg-orange-100 text-orange-700',
      MODERATE: 'bg-yellow-100 text-yellow-700',
      LOW: 'bg-green-100 text-green-700',
      MINIMAL: 'bg-teal-100 text-teal-700'
    };
    return classes[level] || classes.MODERATE;
  };

  const getRiskGradient = (level) => {
    const gradients = {
      CRITICAL: 'from-red-600 via-red-500 to-red-400',
      HIGH: 'from-orange-600 via-orange-500 to-orange-400',
      MODERATE: 'from-amber-600 via-amber-500 to-yellow-400',
      LOW: 'from-green-600 via-green-500 to-emerald-400',
      MINIMAL: 'from-teal-600 via-teal-500 to-cyan-400'
    };
    return gradients[level] || gradients.MODERATE;
  };

  const getRiskLevelText = (level) => {
    const key = level?.toLowerCase();
    return t(`riskLevels.${key}`, level?.replace('_', ' '));
  };

  const getTimelineText = (horizon) => {
    const key = horizon?.toLowerCase();
    return t(`results.timeline.${key}`, t('results.timeline.varies'));
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const userAnswers = JSON.parse(localStorage.getItem('lab2040_assessment_answers') || '{}');
      await generateReportPDF(results, userAnswers);
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert(t('results.pdfError'));
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">{t('results.noResults')}</h2>
          <p className="text-gray-600 mb-6">{t('results.takeAssessmentPrompt')}</p>
          <Link to="/assessment" className="px-6 py-3 bg-teal-500 text-white font-bold rounded-xl hover:bg-teal-700 transition-colors">
            {t('cta.takeAssessment')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Use shared Header component with language switcher */}
      <Header variant="dark" />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero with Speedometer */}
        <div className={`bg-gradient-to-br ${getRiskGradient(results.riskLevel)} rounded-3xl p-6 md:p-8 text-white mb-8`}>
          <p className="text-white/70 uppercase tracking-widest text-sm mb-6">{t('results.heroTitle')}</p>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="flex items-center gap-6">
              <RiskSpeedometer score={results.compositeScore} />
              <div>
                <div className="flex items-baseline">
                  <span className="text-6xl md:text-7xl font-bold" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>{results.compositeScore}</span>
                  <span className="text-2xl text-white/50 ml-1">/100</span>
                </div>
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold uppercase ${getRiskClass(results.riskLevel)} mt-2`}>
                  {getRiskLevelText(results.riskLevel)} {t('riskLevels.risk')}
                </div>
              </div>
            </div>
            
            <RadarChart data={results} t={t} />
          </div>
          
          <div className="flex items-center gap-2 text-white/80 mt-6">
            <Clock className="w-4 h-4" />
            <span>{t('results.expectedImpact')}: {getTimelineText(results.timeHorizon)}</span>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: '🏢', value: results.industryScore, labelKey: 'results.stats.industryRisk', descKey: 'results.stats.industryRiskDesc' },
            { icon: '📋', value: results.riskScore, labelKey: 'results.stats.taskRisk', descKey: 'results.stats.taskRiskDesc' },
            { icon: '🧠', value: results.knowledgeScore, labelKey: 'results.stats.aiKnowledge', descKey: 'results.stats.aiKnowledgeDesc' },
            { icon: '🎯', value: results.readinessScore, labelKey: 'results.stats.readiness', descKey: 'results.stats.readinessDesc' }
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-sm">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="font-serif text-3xl font-bold text-gray-900">{stat.value || 0}%</div>
              <div className="text-sm font-medium text-gray-700 mt-1">{t(stat.labelKey)}</div>
              <div className="text-xs text-gray-500 mt-1">{t(stat.descKey)}</div>
            </div>
          ))}
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Key Risk Factors */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="font-serif text-xl font-bold text-gray-900">{t('results.keyRiskFactors')}</h3>
            </div>
            {results.keyRiskFactors?.length > 0 ? (
              <ul className="space-y-3">
                {results.keyRiskFactors.map((factor, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <span className="text-red-500 mt-1 font-bold">•</span>
                    {factor}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">{t('results.noRiskFactors')}</p>
            )}
          </div>

          {/* Protective Factors */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-serif text-xl font-bold text-gray-900">{t('results.protectiveFactors')}</h3>
            </div>
            {results.protectiveFactors?.length > 0 ? (
              <ul className="space-y-3">
                {results.protectiveFactors.map((factor, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    {factor}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">{t('results.noProtectiveFactors')}</p>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-serif text-xl font-bold text-gray-900">{t('results.recommendations')}</h3>
          </div>
          {results.recommendations?.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {results.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-bold text-sm">{i + 1}</span>
                  </div>
                  <p className="text-gray-700">{rec}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">{t('results.noRecommendations')}</p>
          )}
        </div>

        {/* 90-Day Action Plan CTA */}
        <div className="bg-gradient-to-r from-teal-600 to-gray-700 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-white">{t('results.actionPlan.title')}</h3>
                <p className="text-indigo-200">{t('results.actionPlan.subtitle')}</p>
              </div>
            </div>
            <Link 
              to="/action-plan" 
              className="flex items-center gap-2 px-6 py-3 bg-white text-teal-600 font-bold rounded-xl hover:bg-indigo-50 transition-all"
            >
              {t('results.actionPlan.viewFull')}
              <ChevronRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('nav.dashboard')}</div>
              <div className="text-sm text-gray-500">{t('results.quickLinks.viewAllAssessments')}</div>
            </div>
          </Link>
          
          <Link to="/assessment" className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{t('results.quickLinks.newAssessment')}</div>
              <div className="text-sm text-gray-500">{t('results.quickLinks.trackProgress')}</div>
            </div>
          </Link>
          
          <button 
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-left disabled:opacity-50"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {pdfLoading ? t('results.quickLinks.generating') : t('results.quickLinks.downloadPdf')}
              </div>
              <div className="text-sm text-gray-500">{t('results.quickLinks.saveReport')}</div>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
};

export default ResultsFull;
